import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Original images (fallback)
import roofRepairs from "@/assets/service-roof-repairs-new.png";
import roofMaintenance from "@/assets/service-roof-maintenance-new.png";
import roofReplacement from "@/assets/service-roof-replacement-new.jpg";
import skylightsVentilation from "@/assets/service-skylights-ventilation-new.png";
import newConstruction from "@/assets/service-new-construction-new.png";

// Responsive WebP variants - Small (400w)
import roofRepairsSmall from "@/assets/service-roof-repairs-new.png?w=400&format=webp";
import roofMaintenanceSmall from "@/assets/service-roof-maintenance-new.png?w=400&format=webp";
import roofReplacementSmall from "@/assets/service-roof-replacement-new.jpg?w=400&format=webp";
import skylightsVentilationSmall from "@/assets/service-skylights-ventilation-new.png?w=400&format=webp";
import newConstructionSmall from "@/assets/service-new-construction-new.png?w=400&format=webp";

// Responsive WebP variants - Medium (800w)
import roofRepairsMedium from "@/assets/service-roof-repairs-new.png?w=800&format=webp";
import roofMaintenanceMedium from "@/assets/service-roof-maintenance-new.png?w=800&format=webp";
import roofReplacementMedium from "@/assets/service-roof-replacement-new.jpg?w=800&format=webp";
import skylightsVentilationMedium from "@/assets/service-skylights-ventilation-new.png?w=800&format=webp";
import newConstructionMedium from "@/assets/service-new-construction-new.png?w=800&format=webp";

// Responsive WebP variants - Large (1200w)
import roofRepairsLarge from "@/assets/service-roof-repairs-new.png?w=1200&format=webp";
import roofMaintenanceLarge from "@/assets/service-roof-maintenance-new.png?w=1200&format=webp";
import roofReplacementLarge from "@/assets/service-roof-replacement-new.jpg?w=1200&format=webp";
import skylightsVentilationLarge from "@/assets/service-skylights-ventilation-new.png?w=1200&format=webp";
import newConstructionLarge from "@/assets/service-new-construction-new.png?w=1200&format=webp";

interface ServiceCardProps {
  image: string;
  srcSet: string;
  title: string;
  description: string;
  route: string;
  index: number;
  imagePosition?: string;
  isVisible: boolean;
}

const ServiceCard = ({ image, srcSet, title, description, route, index, imagePosition = "center", isVisible }: ServiceCardProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [transform, setTransform] = useState({ x: 0, y: 0 });
  const cardRef = useRef<HTMLAnchorElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTransform({ x: x * 8, y: y * 8 });
  };

  const handleMouseLeave = () => {
    setTransform({ x: 0, y: 0 });
  };

  // Calculate stagger delay based on grid position
  const row = Math.floor(index / 3);
  const col = index % 3;
  const staggerDelay = row * 150 + col * 100;

  return (
    <Link
      ref={cardRef}
      to={route}
      className="service-card service-card-vignette relative h-[400px] md:h-[420px] rounded-2xl overflow-hidden cursor-pointer group block"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.95)",
        transition: "all 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
        transitionDelay: `${staggerDelay}ms`,
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Background Image with blur reveal and parallax */}
      <img
        src={image}
        srcSet={srcSet}
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        alt={`${title} service by Altum Contractors Houston TX - professional roofing`}
        className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 ease-out group-hover:scale-105 ${
          imageLoaded ? "opacity-100 blur-0" : "opacity-0 blur-md"
        }`}
        style={{
          objectPosition: imagePosition,
          transform: `translate(${transform.x}px, ${transform.y}px) scale(1.02)`,
          transition: 'transform 0.3s ease-out'
        }}
        loading="lazy"
        onLoad={() => setImageLoaded(true)}
      />

      {/* Gradient Overlay with refined transition */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 transition-all duration-500 group-hover:from-black/85 group-hover:via-black/50" />

      {/* Hover glow effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-primary/20 via-transparent to-transparent" />

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-8 transform transition-transform duration-500 group-hover:translate-y-[-4px]">
        {/* Service Name */}
        <div className="flex items-center gap-2 mb-3">
          <h3
            className="font-heading font-bold text-white text-2xl transition-all duration-300 group-hover:text-white"
            style={{ textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}
          >
            {title}
          </h3>
          <ArrowRight
            className="w-6 h-6 text-white transition-all duration-500 ease-out group-hover:translate-x-2 group-hover:text-primary"
          />
        </div>

        {/* Description */}
        <p
          className="font-body text-white/90 text-base leading-relaxed max-w-[90%] transition-all duration-300 group-hover:text-white"
          style={{ textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}
        >
          {description}
        </p>

        {/* Subtle underline accent on hover */}
        <div className="mt-4 h-0.5 bg-gradient-to-r from-primary to-primary/50 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
      </div>
    </Link>
  );
};

const ServicesSection = () => {
  const { t } = useTranslation('home');
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const services = [
    {
      image: roofRepairs,
      srcSet: `${roofRepairsSmall} 400w, ${roofRepairsMedium} 800w, ${roofRepairsLarge} 1200w`,
      title: t('services.roofRepairs.title'),
      description: t('services.roofRepairs.description'),
      route: "/services/roof-repairs",
      imagePosition: "center 35%",
    },
    {
      image: roofReplacement,
      srcSet: `${roofReplacementSmall} 400w, ${roofReplacementMedium} 800w, ${roofReplacementLarge} 1200w`,
      title: t('services.roofReplacement.title'),
      description: t('services.roofReplacement.description'),
      route: "/services/roof-replacement",
    },
    {
      image: roofMaintenance,
      srcSet: `${roofMaintenanceSmall} 400w, ${roofMaintenanceMedium} 800w, ${roofMaintenanceLarge} 1200w`,
      title: t('services.maintenance.title'),
      description: t('services.maintenance.description'),
      route: "/services/roof-maintenance",
    },
    {
      image: newConstruction,
      srcSet: `${newConstructionSmall} 400w, ${newConstructionMedium} 800w, ${newConstructionLarge} 1200w`,
      title: t('services.newConstruction.title'),
      description: t('services.newConstruction.description'),
      route: "/services/new-construction",
    },
    {
      image: skylightsVentilation,
      srcSet: `${skylightsVentilationSmall} 400w, ${skylightsVentilationMedium} 800w, ${skylightsVentilationLarge} 1200w`,
      title: t('services.enhancements.title'),
      description: t('services.enhancements.description'),
      route: "/services/skylights-ventilation",
    },
  ];

  return (
    <section
      id="services"
      ref={sectionRef}
      className="py-[100px] md:py-[100px] bg-background"
    >
      <div className="container mx-auto px-4 max-w-[1400px]">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="section-eyebrow">{t('services.eyebrow')}</span>
          <h2 className="font-heading font-bold text-navy text-3xl md:text-5xl mb-4">
            {t('services.headline')}
          </h2>
          <p className="font-body text-charcoal/80 text-lg max-w-[700px] mx-auto">
            {t('services.subheadline')}
          </p>
        </div>

        {/* Services Grid - First row of 3 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.slice(0, 3).map((service, index) => (
            <ServiceCard
              key={index}
              image={service.image}
              srcSet={service.srcSet}
              title={service.title}
              description={service.description}
              route={service.route}
              index={index}
              imagePosition={service.imagePosition}
              isVisible={isVisible}
            />
          ))}
        </div>
        {/* Second row - 2 cards centered */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 lg:max-w-[calc(66.666%+0.75rem)] lg:mx-auto">
          {services.slice(3).map((service, index) => (
            <ServiceCard
              key={index + 3}
              image={service.image}
              srcSet={service.srcSet}
              title={service.title}
              description={service.description}
              route={service.route}
              index={index + 3}
              imagePosition={service.imagePosition}
              isVisible={isVisible}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;